import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import { ArrowsRightLeftIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import proEdiService, { type EdiRecord, type OrderLine } from '../../../services/proEdi.service';
import EdiFileActions from '../../../components/EdiFileActions';
import { renderMappedLocationLine, renderMappedSizeTypeLine, renderMappedPortLine, renderMappedScacLine } from '../../../utils/ediOrderColumns';
import EdiLocationsEditDrawer from '../../../components/tablemaint/EdiLocationsEditDrawer';
import EdiEquipmentMapEditDrawer from '../../../components/tablemaint/EdiEquipmentMapEditDrawer';
import EdiScacMapEditDrawer from '../../../components/tablemaint/EdiScacMapEditDrawer';
import PortMapDrawer from '../../../components/PortMapDrawer';
import { getEdiLocation, type EdiLocationRow } from '../../../services/edilocations.service';

interface Props {
  open: boolean;
  /** Numeric or "T"-template pro number. Fetches every EDI record on that pro. */
  pronumber?: number | string;
  /** A single edi_orders recno. Takes precedence over `pronumber` — used by pending
   *  EDI orders, which have no pro yet. Fetches just that record. */
  recno?: number;
  onClose: () => void;
  /** Called after a location mapping is saved from the modal, so the opener can refresh
   *  its own view (e.g. the Pending EDI Orders drill). The modal refetches itself. */
  onMappingSaved?: () => void;
}

/** Which EdiRecord keys hold the mapped-name + edi_locations code for the three
 *  location rows, so their cells get a "mapped location / Map Location" sub-line. */
const LOCATION_MAP_FIELDS: Record<string, { name: keyof EdiRecord; code: keyof EdiRecord }> = {
  pickup: { name: 'pickupMapName', code: 'pickupMapCode' },
  delivery: { name: 'deliveryMapName', code: 'deliveryMapCode' },
  terminate: { name: 'terminateMapName', code: 'terminateMapCode' },
};

/** Which EdiRecord keys hold the mapped port name + raw EDI UN/LOCODE for the two port
 *  rows, so their cells show the mapped port name + re-map pencil, or a "Map Port" action. */
const PORT_MAP_FIELDS: Record<string, { name: keyof EdiRecord; unlocode: keyof EdiRecord }> = {
  portOfOrigin: { name: 'portOfOriginMapName', unlocode: 'portOfOriginUnlocode' },
  portOfDischarge: { name: 'portOfDischargeMapName', unlocode: 'portOfDischargeUnlocode' },
};

/** Keys of EdiRecord whose values are display strings (i.e. everything except
 *  the orderLines array), so compare-table cells always render a string. */
type StringFieldKey = {
  [K in keyof EdiRecord]: EdiRecord[K] extends string ? K : never;
}[keyof EdiRecord];

/** One comparable row: a display label and the EdiRecord key it reads. */
type FieldRow = { label: string; key: StringFieldKey };

// V1 EDI Info display order (cms-dev/lib/main.pl edi_viewinfo @displaydata),
// order-lines sub-table excluded. Multi-value fields (Broker Contact, Pickup,
// Delivery, Terminate) are pre-composed newline-joined by the backend.
const FIELD_ROWS: FieldRow[] = [
  { label: 'EDI Source', key: 'ediSource' },
  { label: 'Move Type', key: 'moveType' },
  { label: 'Container', key: 'container' },
  { label: 'PO', key: 'po' },
  { label: 'Bill To Ref #', key: 'billToRef' },
  { label: 'Broker', key: 'broker' },
  { label: 'Broker Ref #', key: 'brokerRef' },
  { label: 'Broker Contact', key: 'brokerContact' },
  { label: 'BOL', key: 'bol' },
  { label: 'Booking', key: 'booking' },
  { label: 'Dept', key: 'dept' },
  { label: 'Seal #', key: 'seal' },
  { label: 'Pickup #', key: 'pickupNumber' },
  { label: 'Delivery Appt #', key: 'deliveryApptNumber' },
  { label: 'SCAC', key: 'scac' },
  { label: 'Terminal', key: 'terminal' },
  { label: 'Size/Type', key: 'sizeType' },
  { label: 'Lbs/Kgs', key: 'lbsKgs' },
  { label: 'Priority', key: 'priority' },
  { label: 'Vessel/Voyage', key: 'vesselVoyage' },
  { label: 'ETA', key: 'eta' },
  { label: 'LFD', key: 'lfd' },
  { label: 'Cutoff Date', key: 'cutoff' },
  { label: 'Doc Cutoff Date', key: 'docCutoff' },
  { label: 'Pickup', key: 'pickup' },
  { label: 'Delivery', key: 'delivery' },
  { label: 'Terminate', key: 'terminate' },
  { label: 'Port of Origin', key: 'portOfOrigin' },
  { label: 'Port of Discharge', key: 'portOfDischarge' },
  { label: 'Piece Count', key: 'pieceCount' },
  { label: 'Commodity', key: 'commodity' },
  { label: 'Contact Info', key: 'contactInfo' },
  { label: 'Drop', key: 'drop' },
  { label: 'Hazmat', key: 'hazmat' },
  { label: 'Rate', key: 'rate' },
  { label: 'Notes', key: 'notes' },
  { label: 'Comments', key: 'comments' },
  { label: 'Additional Info', key: 'additionalInfo' },
  { label: 'DUNS Number/Shipper Info', key: 'shipperInfo' },
  { label: 'SAP Order #', key: 'sapOrderNumber' },
];

/** Cap on side-by-side columns; some pros carry 90+ EDI records. Keep the most
 *  recent (records arrive oldest→newest, so the tail is newest). */
const MAX_COLS = 12;

type LineCol = {
  key: string;
  header: string;
  align?: 'right' | 'center';
  get: (l: OrderLine) => React.ReactNode;
};

const alignClass = (a?: 'right' | 'center') =>
  a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left';

/**
 * A single EDI record's order lines (V1 edi_viewinfo order-lines sub-table).
 * Optional columns (Batch / Serial / MR PO / Lbs, and diageo's Master/Secondary)
 * render only when some line carries that value. A TOTAL-by-UoM row appears with
 * >1 line; missing-product lines are flagged with ** like V1.
 */
const OrderLinesBlock: React.FC<{ rec: EdiRecord }> = ({ rec }) => {
  const lines = rec.orderLines ?? [];
  if (lines.length === 0) return null;

  const show = {
    batch: lines.some((l) => l.batch),
    serial: lines.some((l) => l.serial),
    mrPo: lines.some((l) => l.mrPo),
    lbs: lines.some((l) => l.lbs),
    master: lines.some((l) => l.masterQty || l.masterUom),
    secondary: lines.some((l) => l.secondaryQty || l.secondaryUom),
  };
  const anyMissing = lines.some((l) => l.missingProduct);

  const cols: LineCol[] = [
    { key: 'lineNumber', header: 'Line #', get: (l) => l.lineNumber },
    {
      key: 'vendorCode',
      header: 'Vendor Code',
      get: (l) => (
        <>
          {l.vendorCode}
          {l.missingProduct && <span className="text-red-600 dark:text-red-400">**</span>}
        </>
      ),
    },
    ...(show.batch ? [{ key: 'batch', header: 'Batch', get: (l: OrderLine) => l.batch }] : []),
    ...(show.serial ? [{ key: 'serial', header: 'Serial #', get: (l: OrderLine) => l.serial }] : []),
    ...(show.mrPo ? [{ key: 'mrPo', header: 'MR PO', get: (l: OrderLine) => l.mrPo }] : []),
    ...(show.lbs ? [{ key: 'lbs', header: 'Lbs', align: 'right' as const, get: (l: OrderLine) => l.lbs }] : []),
    { key: 'qty', header: 'Qty', align: 'right', get: (l) => l.qty },
    { key: 'uom', header: 'UoM', align: 'center', get: (l) => l.uom },
    ...(show.master
      ? [
          { key: 'masterQty', header: 'Master Qty', align: 'right' as const, get: (l: OrderLine) => l.masterQty },
          { key: 'masterUom', header: 'Master UoM', align: 'center' as const, get: (l: OrderLine) => l.masterUom },
        ]
      : []),
    ...(show.secondary
      ? [
          { key: 'secondaryQty', header: 'Secondary Qty', align: 'right' as const, get: (l: OrderLine) => l.secondaryQty },
          { key: 'secondaryUom', header: 'Secondary UoM', align: 'center' as const, get: (l: OrderLine) => l.secondaryUom },
        ]
      : []),
  ];

  const qtyIdx = cols.findIndex((c) => c.key === 'qty');
  // TOTAL by UoM (V1 shows it only with >1 line).
  const totals = new Map<string, number>();
  lines.forEach((l) => totals.set(l.uom, (totals.get(l.uom) ?? 0) + (parseFloat(l.qty) || 0)));
  const showTotals = lines.length > 1;
  const trailingCols = cols.length - (qtyIdx + 2); // cells after the UoM column

  return (
    <div className="border-t border-slate-100 dark:border-gray-800">
      <div className="px-4 py-1.5 bg-slate-50 dark:bg-gray-800/60 text-[0.65625rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400">
        Order Lines — {rec.editype || '—'}
        {rec.processDatetime ? ` · ${rec.processDatetime}` : ''}
      </div>
      <div className="overflow-x-auto">
        <table className="border-collapse text-[0.75rem] w-full">
          <thead>
            <tr className="bg-slate-100 dark:bg-gray-800">
              {cols.map((c) => (
                <th
                  key={c.key}
                  className={`px-3 py-1.5 ${alignClass(c.align)} text-[0.65625rem] font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider border-b border-slate-200 dark:border-gray-700 whitespace-nowrap`}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i} className="hover:bg-slate-50 dark:hover:bg-gray-800/50">
                {cols.map((c) => (
                  <td
                    key={c.key}
                    className={`px-3 py-1 ${alignClass(c.align)} text-slate-700 dark:text-gray-300 border-b border-slate-100 dark:border-gray-800 whitespace-nowrap`}
                  >
                    {c.get(l)}
                  </td>
                ))}
              </tr>
            ))}
            {showTotals &&
              [...totals.entries()].map(([uom, sum], i) => (
                <tr key={`total-${uom}`} className="font-semibold text-slate-800 dark:text-gray-100">
                  {i === 0 && (
                    <td colSpan={qtyIdx} className="px-3 py-1 border-t border-slate-300 dark:border-gray-600">
                      TOTAL
                    </td>
                  )}
                  <td className="px-3 py-1 text-right border-t border-slate-300 dark:border-gray-600">
                    {Number.isInteger(sum) ? sum : sum.toFixed(2)}
                  </td>
                  <td className="px-3 py-1 text-center border-t border-slate-300 dark:border-gray-600">{uom}</td>
                  {trailingCols > 0 && <td colSpan={trailingCols} className="border-t border-slate-300 dark:border-gray-600" />}
                </tr>
              ))}
            {anyMissing && (
              <tr>
                <td colSpan={cols.length} className="px-3 py-1 text-[0.6875rem] text-red-600 dark:text-red-400">
                  ** Missing Product Information
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * EDI Info modal (V1 "View EDI" parity, cms-dev edi_viewinfo). Opened from the
 * "View" link beside Created From / Parent on the Addl Info tab. Renders a
 * Revisions-style compare table — fields down the left, one column per EDI record
 * (oldest→newest) — with cells that differ from the column to their left
 * highlighted amber. "Changes only" hides rows unchanged across the set; rows
 * empty across all records are hidden by default. Portals to <body> so the
 * overlay escapes the drawer's transform/stacking context (z-[120] over the
 * drawer's z-[100]).
 */
const EdiInfoModal: React.FC<Props> = ({ open, pronumber, recno, onClose, onMappingSaved }) => {
  const [records, setRecords] = useState<EdiRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [changesOnly, setChangesOnly] = useState(false);
  // Location mapping (self-contained): a "Map Location" click fetches the edi_locations
  // row and opens the shared edit drawer; on save, bump the nonce to refetch this modal.
  const [mapRow, setMapRow] = useState<EdiLocationRow | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const openMap = useCallback((code: number) => {
    getEdiLocation(code).then(setMapRow).catch(() => { /* leave modal as-is on lookup error */ });
  }, []);
  // Size/Type mapping (self-contained): opens the edi_equipmentmap editor by recno.
  const [mapEquipRecno, setMapEquipRecno] = useState<number | null>(null);
  const openMapEquip = useCallback((recno: number) => setMapEquipRecno(recno), []);
  // Port mapping (self-contained): opens the port-map drawer for that EDI unlocode.
  const [mapPortUnlocode, setMapPortUnlocode] = useState<string | null>(null);
  const openMapPort = useCallback((unlocode: string) => setMapPortUnlocode(unlocode), []);
  // SCAC mapping (self-contained): opens the edi_scacmap editor by recno.
  const [mapScacRecno, setMapScacRecno] = useState<number | null>(null);
  const openMapScac = useCallback((recno: number) => setMapScacRecno(recno), []);

  // Esc closes; lock body scroll while open (app modal convention).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open || (recno == null && pronumber == null)) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setChangesOnly(false);
    // recno takes precedence: a pending EDI order has no pro, so it fetches the one record.
    const fetchRecords = recno != null
      ? proEdiService.getEdiByRecno(recno)
      : proEdiService.getProEdi(pronumber as number | string);
    fetchRecords
      .then((data) => { if (!cancelled) setRecords(data); })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, pronumber, recno, refreshNonce]);

  // Cap columns to the most recent MAX_COLS (tail = newest).
  const shown = useMemo(
    () => (records.length > MAX_COLS ? records.slice(records.length - MAX_COLS) : records),
    [records],
  );
  const truncated = records.length - shown.length;
  const canDiff = shown.length > 1;

  // Per-row values + change flags. changedAt[i] compares to the column at i-1.
  const computed = useMemo(() => FIELD_ROWS.map((row) => {
    const values = shown.map((rec) => rec[row.key] ?? '');
    const changedAt = values.map((v, i) => i > 0 && v !== values[i - 1]);
    // SCAC / Size-Type cells render a "Map X" action even when the display value is blank
    // (unmapped) — so a blank-but-mappable row must NOT be treated as empty, or the
    // empty-row filter would hide the row and the user could never open the mapping editor.
    const mappable =
      (row.key === 'scac' && shown.some((rec) => Number(rec.scacMapCode) > 0)) ||
      (row.key === 'sizeType' && shown.some((rec) => Number(rec.equipmentMapCode) > 0));
    return {
      label: row.label,
      key: row.key,
      values,
      changedAt,
      hasChange: changedAt.some(Boolean),
      allEmpty: values.every((v) => !v) && !mappable,
    };
  }), [shown]);

  // Default: hide rows empty in every record. "Changes only": keep only changed rows.
  const visibleRows = changesOnly
    ? computed.filter((r) => r.hasChange)
    : computed.filter((r) => !r.allEmpty);

  // Horizontal scroll affordances — the compare grid overflows with many records.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 1);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(updateArrows);
      ro.observe(el);
    }
    return () => { el.removeEventListener('scroll', updateArrows); ro?.disconnect(); };
  }, [updateArrows, records, changesOnly, open]);

  const scrollByStep = (dir: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(240, el.clientWidth * 0.7), behavior: 'smooth' });
  };

  // Read-only EDI viewer: Alt+C closes; the layer keeps Alt+S off the Pro drawer.
  useAltHotkeys({ C: onClose }, undefined, { layer: open });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[120] bg-slate-900/40 flex items-center justify-center p-6"
      role="dialog"
      aria-label="EDI Info"
      // Close only on a direct backdrop click. React synthetic events bubble through the
      // React tree (portals included), so without this guard a click inside the mapping
      // drawer or its portaled location typeahead would bubble here and close the modal.
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-slate-200 dark:border-gray-700 w-full max-w-[1100px] max-h-[88vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader
          title={
            <span className="inline-flex items-center gap-1.5">
              <ArrowsRightLeftIcon className="h-4 w-4 text-blue-300" />
              EDI Info{records.length > 0 ? ` — ${records.length} record${records.length === 1 ? '' : 's'}` : ''}
            </span>
          }
          onClose={onClose}
          dragHandleProps={dragHandleProps}
          actions={
            canDiff ? (
              <label className="flex items-center gap-1.5 text-[0.71875rem] text-slate-200 cursor-pointer select-none">
                <input type="checkbox" checked={changesOnly} onChange={(e) => setChangesOnly(e.target.checked)} />
                Changes only
              </label>
            ) : undefined
          }
        />

        {/* Body — bounded height so the horizontal scrollbar sits at the bottom
            of the visible area (not buried below a very tall table). Nested flex
            (not h-full) so the scroll child gets a real height and scrolls both
            axes while the footer stays pinned. */}
        <div className="relative flex-1 min-h-0 flex flex-col">
          {canLeft && (
            <button
              type="button"
              aria-label="Scroll left"
              onClick={() => scrollByStep(-1)}
              className="absolute left-0 top-0 bottom-0 z-30 w-7 flex items-center justify-center bg-gradient-to-r from-white/95 dark:from-gray-900/95 to-transparent text-slate-500 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white"
            >
              <ChevronLeftIcon className="h-5 w-5" strokeWidth={2.5} />
            </button>
          )}
          {canRight && (
            <button
              type="button"
              aria-label="Scroll right"
              onClick={() => scrollByStep(1)}
              className="absolute right-0 top-0 bottom-0 z-30 w-7 flex items-center justify-center bg-gradient-to-l from-white/95 dark:from-gray-900/95 to-transparent text-slate-500 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white"
            >
              <ChevronRightIcon className="h-5 w-5" strokeWidth={2.5} />
            </button>
          )}
          <div ref={scrollRef} data-testid="edi-scroll" className="flex-1 min-h-0 overflow-auto">
          {loading && (
            <div className="px-6 py-12 text-center text-slate-400 text-[0.78125rem]">Loading EDI…</div>
          )}
          {!loading && error && (
            <div className="px-6 py-8 text-center text-red-600 text-[0.78125rem]">Failed to load EDI: {error}</div>
          )}
          {!loading && !error && records.length === 0 && (
            <div className="px-6 py-12 text-center text-slate-400 text-[0.8125rem]">No EDI records found.</div>
          )}
          {!loading && !error && truncated > 0 && (
            <div className="px-3 py-1.5 text-[0.6875rem] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
              Showing the most recent {shown.length} of {records.length} EDI records.
            </div>
          )}
          {!loading && !error && records.length > 0 && (
            <table className="border-collapse text-[0.75rem]">
              <thead>
                <tr>
                  <th className="sticky left-0 top-0 z-20 bg-slate-100 dark:bg-gray-800 px-3 py-2 text-left text-[0.65625rem] font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider border-b border-r border-slate-200 dark:border-gray-700 min-w-[150px]">
                    Field
                  </th>
                  {shown.map((rec, i) => (
                    <th
                      key={i}
                      className="sticky top-0 z-10 bg-slate-100 dark:bg-gray-800 px-3 py-2 text-left border-b border-slate-200 dark:border-gray-700 min-w-[180px] align-top"
                    >
                      <div className="text-[0.75rem] font-bold text-slate-800 dark:text-gray-100">{rec.editype || '—'}</div>
                      <div className="text-[0.65625rem] font-normal text-slate-400 dark:text-gray-500">{rec.processDatetime || '—'}</div>
                      {rec.nasEdifilename && rec.recno > 0 && (
                        <div className="mt-1"><EdiFileActions recno={rec.recno} nasEdifilename={rec.nasEdifilename} /></div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr key={row.label} className="hover:bg-slate-50 dark:hover:bg-gray-800/50">
                    <td className="sticky left-0 z-10 bg-white dark:bg-gray-900 px-3 py-1.5 font-medium text-slate-500 dark:text-gray-400 border-b border-r border-slate-100 dark:border-gray-800 whitespace-nowrap align-top">
                      {row.label}
                    </td>
                    {row.values.map((val, i) => {
                      const rec = shown[i];
                      const mapField = LOCATION_MAP_FIELDS[row.key];
                      const locLine = mapField && rec
                        ? renderMappedLocationLine(rec[mapField.name], rec[mapField.code], openMap)
                        : null;
                      // Size/Type is a single mapped value (not an EDI value + sub-line):
                      // render the combined value with a re-map pencil, or Map Size/Type.
                      const sizeCell = row.key === 'sizeType' && rec
                        ? renderMappedSizeTypeLine(val, rec.equipmentMapCode, openMapEquip)
                        : undefined;
                      // SCAC resolves through edi_scacmap; full-cell replacement keyed by
                      // scacMapCode (the mappedscac recno) — re-map pencil or "Map SCAC".
                      const scacCell = row.key === 'scac' && rec
                        ? renderMappedScacLine(val, rec.scacMapCode, openMapScac)
                        : undefined;
                      // Ports resolve to the CMS port name (or the raw unlocode + "Map Port"):
                      // a full-cell replacement like Size/Type, keyed by the EDI unlocode.
                      const portField = PORT_MAP_FIELDS[row.key];
                      const portCell = portField && rec
                        ? renderMappedPortLine(rec[portField.name], rec[portField.unlocode], openMapPort)
                        : undefined;
                      const replacement = sizeCell !== undefined ? sizeCell
                        : scacCell !== undefined ? scacCell
                        : portField ? portCell : undefined;
                      const isReplacement = sizeCell !== undefined || scacCell !== undefined || portField !== undefined;
                      return (
                        <td
                          key={i}
                          className={[
                            'px-3 py-1.5 border-b border-slate-100 dark:border-gray-800 align-top whitespace-pre-line break-words',
                            row.changedAt[i]
                              ? 'bg-amber-50 dark:bg-amber-900/30 font-semibold text-slate-900 dark:text-gray-100'
                              : 'text-slate-700 dark:text-gray-300',
                          ].join(' ')}
                        >
                          {isReplacement ? (
                            replacement ?? <span className="text-slate-300 dark:text-gray-600">—</span>
                          ) : (
                            <>
                              {val || <span className="text-slate-300 dark:text-gray-600">—</span>}
                              {locLine && <div className="mt-1">{locLine}</div>}
                            </>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {visibleRows.length === 0 && (
                  <tr>
                    <td colSpan={shown.length + 1} className="px-6 py-10 text-center text-slate-400 text-[0.78125rem]">
                      {changesOnly ? 'No changes between EDI records.' : 'No EDI details to show.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {/* Order lines — one table per record that has them (V1 order-lines sub-table). */}
          {!loading && !error && shown.some((r) => (r.orderLines?.length ?? 0) > 0) && (
            <div className="border-t border-slate-200 dark:border-gray-700">
              {shown.map((rec, i) =>
                (rec.orderLines?.length ?? 0) > 0 ? <OrderLinesBlock key={i} rec={rec} /> : null,
              )}
            </div>
          )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 border-t border-slate-200 dark:border-gray-700 flex items-center justify-between">
          <div className="text-[0.65625rem] text-slate-400">
            {canDiff && (
              <>
                <span className="inline-block w-2.5 h-2.5 align-middle bg-amber-200 dark:bg-amber-800 rounded-sm mr-1" />
                highlighted = changed from previous EDI
              </>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-[0.75rem] text-slate-600 dark:text-gray-300 bg-panel border border-slate-300 dark:border-gray-600 rounded hover:bg-slate-50 dark:hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>

      {/* Location mapping — the shared EDI Locations edit drawer, opened from a
          "Map Location" sub-line. On save, refetch this modal (nonce) and let the
          opener refresh too. Portals to <body>, so it stacks above this modal. */}
      {mapRow && (
        <EdiLocationsEditDrawer
          row={mapRow}
          onClose={() => setMapRow(null)}
          onSaved={() => { setRefreshNonce((n) => n + 1); onMappingSaved?.(); }}
        />
      )}
      {mapEquipRecno != null && (
        <EdiEquipmentMapEditDrawer
          recno={mapEquipRecno}
          onClose={() => setMapEquipRecno(null)}
          onSaved={() => { setRefreshNonce((n) => n + 1); onMappingSaved?.(); }}
        />
      )}
      {mapPortUnlocode && (
        <PortMapDrawer
          unlocode={mapPortUnlocode}
          onClose={() => setMapPortUnlocode(null)}
          onSaved={() => { setRefreshNonce((n) => n + 1); onMappingSaved?.(); }}
        />
      )}
      {mapScacRecno != null && (
        <EdiScacMapEditDrawer
          recno={mapScacRecno}
          onClose={() => setMapScacRecno(null)}
          onSaved={() => { setRefreshNonce((n) => n + 1); onMappingSaved?.(); }}
        />
      )}
    </div>,
    document.body,
  );
};

export default EdiInfoModal;
